import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Info, MapPin, CheckCircle2, AlertCircle, Mail } from "lucide-react";

export default function Referencement() {
  useEffect(() => {
    document.title = "Référencement et classement des entreprises — Déménageur Pro";
    const meta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    meta("description", "Comment fonctionne l'annuaire Déménageur Pro : origine des données, différence entre entreprise référencée et partenaire, critères de classement, procédures de modification et suppression.");
    meta("robots", "index,follow");
  }, []);

  return (
    <div className="animate-fade-up" data-testid="page-referencement">
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#F7F9FB] to-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-4">
            <Info className="w-3.5 h-3.5" /> Transparence
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-4">
            Référencement et classement des entreprises
          </h1>
          <p className="text-lg text-[#5C6B7A] max-w-3xl">
            Cette page explique comment Déménageur Pro constitue son annuaire, distingue les entreprises partenaires des simples fiches informatives, et classe les résultats affichés au public.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl space-y-12">

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">1. Origine des données</h2>
            <p className="text-[#5C6B7A]">
              L'annuaire Déménageur Pro est constitué à partir de données professionnelles publiques : bases Sirene (INSEE),
              registre des transporteurs routiers (REGISTRE MARCHANDISES), Annuaire des entreprises (data.gouv.fr) et
              informations volontairement fournies par les entreprises partenaires. Chaque fiche indique sa source et
              sa date de dernière vérification.
            </p>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">2. Conditions d'ajout d'une entreprise</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C6B7A]">
              <li>SIRET valide à 14 chiffres et clé de contrôle correcte</li>
              <li>Établissement <strong>actif</strong> confirmé par Sirene/INSEE</li>
              <li>Code APE/NAF cohérent avec l'activité de déménagement (49.42Z ou activité complémentaire)</li>
              <li>Adresse et dénomination correspondant aux données officielles</li>
              <li>Aucune fiche n'est ajoutée sur la base de données de démonstration, seed ou sources non officielles</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">3. Différence entre entreprise référencée et partenaire</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 border-2 border-[#E5E7EB] rounded-2xl bg-white">
                <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B7A] mb-2">Référencée</div>
                <p className="text-sm text-[#5C6B7A]">Fiche informative issue de données publiques. <strong>Ne reçoit aucun lead</strong>. Peut demander modification ou retrait à tout moment.</p>
              </div>
              <div className="p-5 border-2 border-[#2BD391] rounded-2xl bg-[#F0FBF6]">
                <div className="text-xs font-bold uppercase tracking-wider text-[#1AA66C] mb-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Partenaire vérifié</div>
                <p className="text-sm text-[#2C3E50]">Entreprise ayant fourni ses justificatifs (SIRET, assurance, licence de transport). <strong>Reçoit les leads correspondant à sa zone.</strong></p>
              </div>
              <div className="p-5 border-2 border-[#FBBF24] rounded-2xl bg-[#FEF9E7]">
                <div className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> En vérification</div>
                <p className="text-sm text-[#2C3E50]">Fiche masquée du public tant que la correspondance officielle SIRET/RNE n'est pas confirmée.</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">4. Vérifications effectuées</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C6B7A]">
              <li>Validité du numéro SIRET (14 chiffres + clé)</li>
              <li>Statut actif ou fermé via Sirene/INSEE</li>
              <li>Cohérence dénomination / adresse / activité</li>
              <li>Pour les partenaires : justificatif d'assurance, licence de transport, RIB, contrat signé</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">5. Fréquence de mise à jour</h2>
            <p className="text-[#5C6B7A]">
              Les fiches partenaires font l'objet d'une vérification lors de la signature puis annuellement.
              Les fiches référencées sont revérifiées à chaque signalement et, par lot, au moins une fois par an
              via l'API Sirene. La date de dernière vérification est visible sur chaque fiche.
            </p>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">6. Critères de classement dans les résultats publics</h2>
            <p className="text-[#5C6B7A] mb-3">Les entreprises sont classées dans l'ordre suivant :</p>
            <ol className="list-decimal pl-6 space-y-2 text-[#5C6B7A]">
              <li>Correspondance géographique avec la ville ou le département consulté</li>
              <li>Établissement actif et informations officiellement vérifiées</li>
              <li>Pertinence réelle de l'activité de déménagement (code APE)</li>
              <li>Complétude de la fiche (téléphone, adresse, site web renseignés)</li>
              <li>Date de dernière vérification (plus récente = mieux classée)</li>
              <li>Ordre alphabétique en cas d'égalité</li>
            </ol>
          </div>

          <div>
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">7. Influence du statut partenaire et du paiement</h2>
            <p className="text-[#5C6B7A] mb-3">
              À ce jour, <strong>il n'y a pas de placement publicitaire payant dans l'annuaire.</strong>
              Le paiement d'un lead (12,50 € HT, cf. CGV partenaires) ne modifie pas la position d'une entreprise
              dans le classement public.
            </p>
            <p className="text-[#5C6B7A]">
              Toute future mise en avant payante sera clairement identifiée par la mention
              <span className="mx-1 px-2 py-0.5 bg-[#2C3E50] text-white text-xs rounded font-semibold">Sponsorisé</span>
              ou
              <span className="mx-1 px-2 py-0.5 bg-[#2C3E50] text-white text-xs rounded font-semibold">Partenaire mis en avant</span>.
            </p>
          </div>

          <div className="bg-[#F7F9FB] border border-[#E5E7EB] rounded-2xl p-6">
            <h2 className="font-heading font-bold text-2xl text-[#2C3E50] mb-3">8. Demandes de modification, suppression ou signalement</h2>
            <p className="text-[#5C6B7A] mb-4">
              Toute entreprise référencée peut demander à tout moment :
            </p>
            <ul className="list-disc pl-6 space-y-1 text-[#5C6B7A] mb-4">
              <li>La modification d'informations inexactes</li>
              <li>La suppression totale de sa fiche</li>
              <li>Le signalement d'un contenu erroné</li>
            </ul>
            <p className="text-sm text-[#5C6B7A]">
              Adresse dédiée : <a href="mailto:demenageurpro.contact@gmail.com" className="text-[#1AA66C] font-semibold hover:underline">demenageurpro.contact@gmail.com</a>
              {" "} — ou via le formulaire disponible en bas de chaque fiche.
            </p>
            <Link to="/legal/correction" className="inline-block mt-3 btn-outline text-sm" data-testid="ref-correction-link">
              Formulaire de correction / suppression
            </Link>
          </div>

          <p className="text-xs text-[#8A98A5] italic text-center mt-8">
            Dernière mise à jour de cette page : février 2026. Contact : demenageurpro.contact@gmail.com
          </p>
        </div>
      </section>
    </div>
  );
}
